import React from 'react';
import { LuMountain, LuWaves, LuLeaf, LuCompass, LuBird, LuCoffee, LuMap, LuUsers, LuHome, LuSnowflake, LuWifi, LuUtensils, LuSunrise, LuShowerHead, LuShirt, LuHeart, LuSun, LuStar, LuFlame, LuMusic } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageWrapper } from '../components/PageWrapper';
import PageHero from '../components/PageHero';

const amenities = [
  { category: 'Nature & Adventure', icon: <LuMountain />, items: [
    { name: 'Chembra Peak Trekking', desc: 'Wayanad\'s highest peak (2,100m) — guided or self-guided, 15km from the villa', icon: <LuMountain /> },
    { name: 'Soojipara Waterfalls', desc: '3-tiered waterfall, 200m high, just 1.5km away via scenic walkway', icon: <LuWaves /> },
    { name: 'River Trekking', desc: 'Crystal-clear mountain stream adjacent to property — non-monsoon swimming', icon: <LuWaves /> },
    { name: 'Forest Walks', desc: 'Guided nature walks through Western Ghats biodiversity hotspot', icon: <LuLeaf /> },
    { name: 'Longest Zipline', desc: 'Exciting zipline adventure 1.2km from the property', icon: <LuCompass /> },
    { name: 'Bird Watching', desc: 'Rich avian diversity — rare and endemic species in the Western Ghats', icon: <LuBird /> },
  ]},
  { category: 'Experiences', icon: <LuCompass />, items: [
    { name: 'Tea Factory Visit', desc: 'Harrison Tea Factory 1.5km away — see how Wayanad\'s famous tea is made', icon: <LuCoffee /> },
    { name: 'Spice Plantation Tour', desc: 'Visit cardamom, pepper, and coffee plantations in the village', icon: <LuLeaf /> },
    { name: 'Neelimala Viewpoint', desc: 'Mesmerizing view of Meenmutti Waterfalls, 6km away', icon: <LuSunrise /> },
    { name: 'Karapuzha Dam', desc: 'One of India\'s largest earthen dams, a scenic day trip', icon: <LuMap /> },
    { name: 'Glass Bridge Park', desc: 'Adventure park with glass bridge, archery, gun shooting — 8km', icon: <LuCompass /> },
    { name: 'Cultural Tours', desc: 'Arranged visits to tribal communities and heritage sites', icon: <LuUsers /> },
  ]},
  { category: 'Comfort & Stay', icon: <LuHome />, items: [
    { name: 'AC Rooms', desc: 'All rooms equipped with air conditioning for year-round comfort', icon: <LuSnowflake /> },
    { name: 'Free High-Speed WiFi', desc: 'Reliable broadband throughout the property', icon: <LuWifi /> },
    { name: 'Home-Cooked Meals', desc: 'Authentic Kerala breakfast, lunch and dinner made with love', icon: <LuUtensils /> },
    { name: 'Private Balconies', desc: 'Select rooms feature private sit-outs with panoramic views', icon: <LuSunrise /> },
    { name: 'Hot Water', desc: '24-hour hot water in all en-suite bathrooms', icon: <LuShowerHead /> },
    { name: 'Laundry Service', desc: 'Available on request for extended stays', icon: <LuShirt /> },
  ]},
  { category: 'Wellness & Relaxation', icon: <LuHeart />, items: [
    { name: 'Sunrise Views', desc: 'Watch the sun rise over the Chembra Peak from your room — unobstructed', icon: <LuSun /> },
    { name: 'Herbal Tea Garden', desc: 'Harvest and brew fresh herbal teas from the property garden', icon: <LuLeaf /> },
    { name: 'Meditation Spaces', desc: 'Quiet corners to meditate amid forest sounds and mountain air', icon: <LuHeart /> },
    { name: 'Night Sky Gazing', desc: 'Zero light pollution at 900m altitude — spectacular starry skies', icon: <LuStar /> },
    { name: 'Evening Bonfires', desc: 'Cozy bonfire evenings on request under the stars', icon: <LuFlame /> },
    { name: 'Nature Soundscape', desc: 'Perennial streams, birdsong, and wind through tea gardens', icon: <LuMusic /> },
  ]},
];

export default function Amenities() {
  return (
    <PageWrapper>
      <Helmet>
        <title>Activities & Experiences – Villa Tesori | Wayanad Adventure Stay</title>
        <meta name="description" content="Explore top activities in Wayanad at Villa Tesori. Chembra Peak trekking, Soojipara Falls, ziplining, and authentic Kerala experiences in Vellarimala." />
        <meta name="keywords" content="activities in Wayanad, Chembra Peak trekking, Soojipara Falls distance, zipline Wayanad, best things to do in Wayanad, luxury experiences Kerala" />
        <meta property="og:title" content="Experiences & Amenities – Villa Tesori Wayanad" />
        <meta property="og:description" content="From mountain treks to bonfire nights. Discover the best of Wayanad's nature and adventure." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://villatesori.com/amenities" />
        <link rel="canonical" href="https://villatesori.com/amenities" />
      </Helmet>

      <PageHero
        scriptText="Indulge & Explore"
        title="Amenities & Experiences"
        subtitle="From misty mountain treks to home-cooked feasts — every day is an adventure"
        bgImage="/img8.jpeg"
      />

      <section className="section">
        <div className="container">
          {amenities.map((cat, ci) => (
            <div key={cat.category} style={{ marginBottom: 80 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 40 }}>
                <span style={{ fontSize: '2.2rem', color: 'var(--gold-light)', display: 'flex', alignItems: 'center' }}>{cat.icon}</span>
                <div>
                  <div style={{ width: 40, height: 1, background: 'var(--gold)', marginBottom: 8 }} />
                  <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.2rem', color: 'var(--forest)' }}>{cat.category}</h2>
                </div>
              </div>
              <div className="grid-3">
                {cat.items.map(item => (
                  <div key={item.name} style={{
                    display: 'flex', gap: 16, padding: '24px 24px',
                    background: ci % 2 === 0 ? 'var(--cream)' : 'var(--white)',
                    border: '1px solid var(--mist)', borderRadius: 2,
                    transition: 'all 0.3s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--sage)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--mist)'; e.currentTarget.style.transform = 'none'; }}
                  >
                    <span style={{ fontSize: '1.6rem', flexShrink: 0, marginTop: 2, color: 'var(--gold)', display: 'flex', alignItems: 'center' }}>{item.icon}</span>
                    <div>
                      <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.15rem', color: 'var(--forest)', marginBottom: 6 }}>{item.name}</h4>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.7 }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-sm" style={{ background: 'var(--forest)', textAlign: 'center' }}>
        <div className="container-sm">
          <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.6rem', color: 'var(--mist)', marginBottom: 16 }}>
            Ready to Experience It All?
          </h2>
          <p style={{ color: 'rgba(200,217,192,0.7)', marginBottom: 32 }}>Every experience at Villa Tesori is personal, guided by your hosts who know every trail and taste.</p>
          <Link to="/contact" className="btn btn-gold">Book Your Stay</Link>
        </div>
      </section>
    </PageWrapper>
  );
}
